import { FC, useEffect, useMemo, useState } from "react";
import { Modal } from "@app/shared/Modals";
import { QESTextArea } from "@app/shared/qes-standards/forms/TextArea";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";

interface PublishProjectsModalProps {
  open: boolean;
  /** Selected projects whose names are resolvable from the loaded rows. */
  projects: { projectId: number; name: string }[];
  /** Total selected count; may exceed `projects.length` when Select All spans not-yet-loaded pages. */
  totalCount: number;
  /** Disables the Publish button while a dispatch is in flight. */
  isPublishing: boolean;
  onCancel: () => void;
  onPublish: (comment: string) => void;
}

const noun = (count: number): string => (count === 1 ? "project" : "projects");

/**
 * Confirmation modal for the bulk Publish action: lists the selected projects, warns that published results are
 * visible to everyone with network access, and collects an optional comment. The comment is passed through
 * unchanged (matching the classic Project Actions flyout, which sends the raw value — empty string when blank —
 * and applies no length limit).
 */
export const PublishProjectsModal: FC<PublishProjectsModalProps> = ({
  open,
  projects,
  totalCount,
  isPublishing,
  onCancel,
  onPublish,
}) => {
  const [comment, setComment] = useState("");

  // Reset the draft each time the modal opens so a prior comment doesn't leak into a new publish.
  useEffect(() => {
    if (open) setComment("");
  }, [open]);

  // Select All can span pages that aren't loaded, so we may not have every name — surface the remainder as a count.
  const unnamedCount = Math.max(0, totalCount - projects.length);

  const body = useMemo(() => (
    <div className="network-portfolio-management-publish-modal" data-testid="npm-publish-modal">
      <p className="network-portfolio-management-publish-modal__summary">
        Publishing <strong>{totalCount} {noun(totalCount)}</strong>:
      </p>
      <ul className="network-portfolio-management-publish-modal__projects" data-testid="npm-publish-modal-projects">
        {projects.map((p) => (
          <li key={p.projectId}>{p.name}</li>
        ))}
        {unnamedCount > 0 && (
          <li className="network-portfolio-management-publish-modal__more">…and {unnamedCount} more</li>
        )}
      </ul>
      <p className="network-portfolio-management-publish-modal__note">
        <QesIcon iconName={QesIcons.InfoCircle} isDecorative width="16" />
        Published results will be visible to everyone with access to these networks.
      </p>
      <QESTextArea
        label="Comment (optional)"
        testId="npm-publish-modal-comment"
        placeholder="Add a publish comment (optional)..."
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        containerClassName="network-portfolio-management-publish-modal__comment"
      />
    </div>
  ), [projects, totalCount, unnamedCount, comment]);

  return (
    <Modal
      isVisible={open}
      closeModal={onCancel}
      hideBodyScroll
      content={{
        body,
        cancelButtonText: "Cancel",
        confirmButtonAction: () => onPublish(comment),
        confirmButtonDisable: isPublishing,
        confirmButtonText: "Publish",
        confirmButtonType: "primary",
        title: "Publish Projects",
      }}
    />
  );
};
